export interface UserInfo {
  id: string;
  username: string;
  alias: string;
  password_set_on: string;
  registered_by: string;
  organization?: { id: string; name: string };
  organizations?: { id: string; name: string }[];
  role: "User" | "GameMaster" | "Owner";
  terms_accepted: boolean;
  disabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface OrganisationInfo {
  ID: string;
  Name: string;
  StripeCustomerID: string;
  OwnerID: string;
  CreatedAt: string;
  UpdatedAt: string;
  DeletedAt: string | null;
  ExpireAt: string;
}

export interface SubscriptionInfo {
  ID: string;
  StripeID: string;
  OrganizationID: string;
  PlanName: string;
  AutoRenew: boolean;
  ActiveAt: string;
  CanceledAt: string | null;
  ExpireAt: string;
}

export interface GameDownloadInfo2 {
  version: string;
  upload_date: string;
  files: {
    key: string;
    platform: string;
    upload_date: string;
    version: string;
    size: number;
  }[];
}

export interface AntSelectOptionProps {
  label: string;
  value: string;
}

export interface ActivityMatchInfo {
  id: string;
  name: string;
  description: string;
  status: string;
  start_at: string;
  end_at: string;
  match_type: string;
  options: ActivityMatchOptions;
  allow_register: boolean;
  display_policy: string;
  match_tz: number;
  max_court_count: number;
  participant_count: number;
  expired_at: string;
}

export interface ActivityMatchOptions {
  location: string;
  display_name: string;
  logo: string;
  start_time: {date: string, start_time: string}[]
}

export interface ActivityCategoryInfo {
  id: number;
  name: string;
  round_names: string[];
  options: MatchCategoryOptions;
  need_reload: boolean;
  participant_count: number;
  has_extra_matches: boolean;
  is_setup_done: boolean;
  is_schedule_done: boolean;
  is_bracket_done: boolean;
  bracket_status?: 'not_created' | 'draft' | 'published';
  bracket_adjustment_mode?: BracketAdjustmentMode | '';
  bracket_recommended_adjustment_mode?: BracketAdjustmentMode;
  bracket_allowed_adjustment_modes?: BracketAdjustmentMode[];
  bracket_round_names_by_mode?: Partial<Record<BracketAdjustmentMode, string[]>>;
  bracket_revision?: number;
  bracket_published_at?: string;
}

export type BracketAdjustmentMode = 'perfect' | 'bye' | 'extra';

export interface BracketDraftSlot {
  key: string;
  kind: 'participant' | 'bye' | 'qualifier';
  phase: 'preliminary' | 'main';
  round: number;
  match_index: number;
  side: number;
  display_position: number;
  source_match_index?: number;
  participant_id?: string;
  is_seed_locked: boolean;
  group_key?: string;
  court?: number;
  plan_date?: string;
  plan_start_at?: string;
  plan_end_at?: string;
  bout_number?: string;
}

export interface BracketDraftInfo {
  category_id: number;
  status: 'not_created' | 'draft' | 'published';
  adjustment_mode: BracketAdjustmentMode;
  recommended_adjustment_mode: BracketAdjustmentMode;
  allowed_adjustment_modes: BracketAdjustmentMode[];
  revision: number;
  competition_locked: boolean;
  published_at?: string;
  round_names: string[];
  participants: MatchParticipant[];
  slots: BracketDraftSlot[];
}

export interface BracketDraftImportRowError {
  // row 0 with an empty column flags a whole-workbook problem.
  row: number;
  column: string;
  message: string;
}

export interface BracketDraftImportAssignment {
  slot_key: string;
  participant_id: string;
  participant_name: string;
  license_number: string;
  is_seed_locked: boolean;
}

export interface BracketDraftImportPreview {
  assignments: BracketDraftImportAssignment[];
  row_errors: BracketDraftImportRowError[];
}

export interface MatchCategoryOptions {
  bracket_structure: string;
  rounds: Record<number, MatchRoundConfig>;
}

export interface MatchRoundScheduleConfig {
  court: number; // 对应哪个会场
  date: string;
  start_at: string;
  end_at: string;
  type: number; // 对应哪个组别 如1对应G1 2对应G2
}

export interface MatchRoundConfig {
  name: string;
  courts: number;
  game_rounds: number;
  match_count: number;
  round_time: number;
  rest_time: number;
  schedule?: MatchRoundScheduleConfig[];
}

export interface ActivityOutletContext {
  activity: ActivityMatchInfo
  categories: ActivityCategoryInfo[] | null
  reloadData: (newAction: string) => void
  rtds_key: string | null,
  tournamentExpiredAt: string | null
  hasUnsavedChanges?: boolean
  setHasUnsavedChanges?: (hasChanges: boolean) => void
}

export interface MatchParticipant {
  id: string;
  display_name: string;
  team_name: string;
  first_name: string;
  last_name: string;
  birth_date: string;
  gender: string;
  license_number: string;
  location: string;
  noc: string;
  category_ids: number[];
  is_setup_done?: boolean;
  is_schedule_done?: boolean;
  has_extra_matches?: boolean;
  draft_slot_key?: string;
  draft_slot_kind?: string;
  is_seed_locked?: boolean;
}

export interface SubscriptionFeature {
  name: string;
  expired_at: string;
  options: any;
}

export interface ActivityScheduleConfig {
  category_id: number;
  round: number;
  group_key: number;
  name: string;
  duration: number;
  used: boolean;
  court: number;
  match_count: number;
  date: string;
  start_at: string;
  end_at: string;
}

export interface MatchRoundResult {
  id: number;
  game_round: number;
  winner_id: string;
  state: string;
  win_score: number;
  lose_score: number;
  duration: number;
  reset_count: number;
  start_at: string;
  end_at: string;
  stats: any;
  is_current: boolean;
  reason: string;
}

export enum MatchType {
  Normal = 0,
  ExtraMatch = 1,
  Final = 2,
  ThirdPlace = 3,
}

export interface ActivityGameMatchInfo {
  id: string;
  state: string;
  court: number;
  bout_number: string;
  rank?: number;
  participants: MatchParticipant[];
  round: number;
  next_match_id: string;
  next_loser_match_id: string;
  category_id: number;
  winner_id: string;
  reason?: string;
  plan_date?: string;
  plan_start_at?: string | null;
  plan_end_at?: string | null;
  type: MatchType;
  group_key?: string;
  preview?: boolean;
  draft_slots?: {
    key: string;
    kind: string;
    side: number;
    participant_id?: string;
    is_seed_locked?: boolean;
    source_match_index?: number;
  }[];
}
